import { useHasPermission, useUTLSettingsContext } from "@dexteel/mesf-core";
import { Alert, Box, Button, CircularProgress, Stack } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";

import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import {
	useSectionSpec,
	useSectionSpecVersion,
} from "@/shared/hooks/useSectionSpec";
import { formatSectionSpecRevision } from "@/shared/utils/sectionSpecRevision";
import type { SectionSpecFileKind } from "@/shared/models/SectionSpec";
import {
	saveSectionSpecFileChange,
	useCanReplaceSectionSpecFile,
} from "../../hooks/useSectionSpecFileRotation";
import { useSectionSpecMutations } from "../../hooks/useSectionSpecMutations";
import {
	formatEffectiveDate,
	toSectionSpecFormValues,
} from "../../models/SectionSpecFormat";
import type { SectionSpecFileChange } from "../../models/SectionSpecRotation";
import {
	getDefaultProfileSpecId,
	getSectionSpecState,
} from "../../models/SectionSpecState";
import { SectionSpecActions } from "./SectionSpecActions";
import { SectionSpecEditorModal } from "./SectionSpecEditorModal";
import type { SectionSpecEditorMode } from "./SectionSpecForm";
import { SectionSpecPublishModal } from "./SectionSpecPublishModal";
import { SectionSpecReadOnlyView } from "./SectionSpecReadOnlyView";
import { SectionSpecRevisionSelect } from "./SectionSpecRevisionSelect";

interface SectionSpecEditorProps {
	sectionId: number;
	assetId: number;
	sectionName: string;
	onError: (error: Error) => void;
}

interface EditorTarget {
	mode: SectionSpecEditorMode;
	profileSpecId: number | null;
}

export const SectionSpecEditor = ({
	sectionId,
	assetId,
	sectionName,
	onError,
}: SectionSpecEditorProps) => {
	useUTLSettingsContext();
	const canEditPublished = useHasPermission()(
		"ui.products.section.specs.edit-published",
	);
	const canRotate = useCanReplaceSectionSpecFile();

	const [selectedId, setSelectedId] = useState<number | null>(null);
	const [editorTarget, setEditorTarget] = useState<EditorTarget | null>(null);
	const [confirmingId, setConfirmingId] = useState<number | null>(null);
	const [deletingId, setDeletingId] = useState<number | null>(null);
	const [toDraftId, setToDraftId] = useState<number | null>(null);

	const {
		data: sectionDetail,
		refetch,
		error: queryError,
		isLoading: queryLoading,
		isFetching: queryFetching,
	} = useSectionSpec(sectionId, assetId);

	const versions = sectionDetail?.versions ?? [];
	const state = sectionDetail
		? getSectionSpecState(sectionDetail, canEditPublished)
		: null;
	const defaultId = sectionDetail
		? getDefaultProfileSpecId(sectionDetail)
		: null;
	const selectedVersion =
		versions.find((version) => version.ProfileSpecId === selectedId) ??
		versions.find((version) => version.ProfileSpecId === defaultId) ??
		null;
	const selectedIsCurrent =
		selectedVersion?.ProfileSpecId === sectionDetail?.spec?.ProfileSpecId;
	const selectedVersionQuery = useSectionSpecVersion(
		selectedIsCurrent ? null : (selectedVersion?.ProfileSpecId ?? null),
	);
	const selectedDetail = selectedIsCurrent
		? sectionDetail
		: selectedVersionQuery.data;
	const selectedValues = selectedDetail
		? toSectionSpecFormValues(selectedDetail)
		: null;
	const loadError = queryError ?? selectedVersionQuery.error;
	const isLoading =
		queryLoading ||
		(!selectedIsCurrent &&
			selectedVersion !== null &&
			selectedVersionQuery.isLoading);

	const finish = (message: string, profileSpecId: number | null) => {
		setSelectedId(profileSpecId);
		enqueueSnackbar(message, { variant: "success" });
	};
	const mutations = useSectionSpecMutations({
		sectionId,
		assetId,
		onError,
		onDone: (message, profileSpecId) => {
			setConfirmingId(null);
			finish(message, profileSpecId);
		},
	});
	const selectedSpec = selectedDetail?.spec ?? null;

	const saveRotated = async (
		kind: SectionSpecFileKind,
		change: SectionSpecFileChange,
	) => {
		if (selectedSpec === null) return;
		await saveSectionSpecFileChange(selectedSpec, kind, change);
		await mutations.refresh(selectedSpec.ProfileSpecId);
		finish("File rotated", selectedSpec.ProfileSpecId);
	};

	const scheduled =
		sectionDetail?.metadata?.NextScheduledPublishedEffectiveDate &&
		sectionDetail.metadata.NextScheduledPublishedRev !== null
			? sectionDetail.metadata
			: null;

	return (
		<Box component="section">
			<Stack spacing={1.25}>
				<Stack
					direction={{ xs: "column", md: "row" }}
					alignItems={{ xs: "stretch", md: "center" }}
					justifyContent="space-between"
					gap={1}
				>
					<Box>
						{selectedVersion && (
							<SectionSpecRevisionSelect
								versions={versions}
								selected={selectedVersion}
								disabled={mutations.isPending}
								loading={queryFetching}
								onSelect={setSelectedId}
							/>
						)}
					</Box>
					{state && (
						<SectionSpecActions
							state={state}
							selectedId={selectedVersion?.ProfileSpecId ?? null}
							disabled={mutations.isPending || isLoading || Boolean(loadError)}
							hasAppliedItem={
								selectedValues?.items.some((row) => row.Applies) ?? false
							}
							onSelect={setSelectedId}
							onCreateDraft={() =>
								setEditorTarget({ mode: "create", profileSpecId: null })
							}
							onEditDraft={(id) =>
								setEditorTarget({ mode: "draft", profileSpecId: id })
							}
							onDeleteDraft={setDeletingId}
							onConfirmDraft={setConfirmingId}
							onSendToDraft={setToDraftId}
							onEditPublished={(id) =>
								setEditorTarget({ mode: "correction", profileSpecId: id })
							}
						/>
					)}
				</Stack>

				{scheduled && (
					<Alert severity="info">
						Scheduled confirmation:{" "}
						{formatSectionSpecRevision(
							scheduled.NextScheduledPublishedRev,
							scheduled.NextScheduledPublishedSubRev,
						)}{" "}
						scheduled for{" "}
						{formatEffectiveDate(scheduled.NextScheduledPublishedEffectiveDate)}
						.
						{scheduled.ActiveDraftProfileSpecId !== null &&
							" An existing draft prevents changing this revision to Draft."}
					</Alert>
				)}

				{loadError ? (
					<Alert
						severity="error"
						action={
							<Button
								color="inherit"
								size="small"
								onClick={() => {
									void refetch();
									void selectedVersionQuery.refetch();
								}}
							>
								Retry
							</Button>
						}
					>
						{loadError.message}
					</Alert>
				) : isLoading ? (
					<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
						<CircularProgress size={28} />
					</Box>
				) : selectedDetail?.spec && selectedValues ? (
					<SectionSpecReadOnlyView
						spec={selectedDetail.spec}
						values={selectedValues}
						onSaveRotated={canRotate ? saveRotated : undefined}
					/>
				) : (
					<Alert severity="info">
						No specification exists yet. Create a draft to enter specifications
						and dimensions.
					</Alert>
				)}
			</Stack>

			{editorTarget && sectionDetail && (
				<SectionSpecEditorModal
					mode={editorTarget.mode}
					sectionId={sectionId}
					assetId={assetId}
					sectionName={sectionName}
					profileSpecId={editorTarget.profileSpecId}
					sectionDetail={sectionDetail}
					onClose={() => setEditorTarget(null)}
					onSaved={async (profileSpecId) => {
						setEditorTarget(null);
						await mutations.refresh(profileSpecId);
						finish(
							editorTarget.mode === "correction"
								? "Published revision updated"
								: "Draft saved",
							profileSpecId,
						);
					}}
					onError={onError}
				/>
			)}
			{confirmingId !== null && (
				<SectionSpecPublishModal
					profileSpecId={confirmingId}
					isPending={mutations.confirm.isPending}
					onClose={() => setConfirmingId(null)}
					onError={onError}
					onPublish={(publication) =>
						mutations.confirm.mutate({
							profileSpecId: confirmingId,
							publication,
						})
					}
				/>
			)}
			<ConfirmationDialogRaw
				open={toDraftId !== null}
				title={
					toDraftId ===
					sectionDetail?.metadata?.NextScheduledPublishedProfileSpecId
						? "Change Scheduled Revision to Draft"
						: "Send Published Revision Back to Draft"
				}
				message={
					toDraftId ===
					sectionDetail?.metadata?.NextScheduledPublishedProfileSpecId
						? "Change scheduled revision to Draft? Its saved specifications, dimensions, and files will be preserved. It will no longer become effective automatically."
						: "Send this published revision back to Draft? Its saved specifications, dimensions, and files will be preserved. The previous revision will be in force again until this one is confirmed, and its revision number can be reused."
				}
				buttons={{ no: "Cancel", yes: "Change to Draft" }}
				onClose={(value) => {
					setToDraftId(null);
					if (value === "OK" && toDraftId !== null)
						mutations.sendToDraft.mutate(toDraftId);
				}}
			/>
			<ConfirmationDialogRaw
				open={deletingId !== null}
				title="Delete Draft"
				message="Delete and discard this draft? Confirmed revisions are unaffected."
				buttons={{ no: "Cancel", yes: "Delete Draft" }}
				onClose={(value) => {
					setDeletingId(null);
					if (value === "OK" && deletingId !== null)
						mutations.remove.mutate(deletingId);
				}}
			/>
		</Box>
	);
};
